import type { CalendarItem, CalendarOverlap } from '../../api/client.ts';
import { ObjectLink, OverlapBadge, RunEntry, windowText } from './CalendarEntry.tsx';
import { dayLabel, daysOf } from './dates.ts';

export interface CalendarBodyProps {
  environmentId: string;
  from: string;
  to: string;
  items: CalendarItem[];
  overlaps: CalendarOverlap[];
  today: string;
}

export const runsByDay = (items: CalendarItem[]) => {
  const out = new Map<string, CalendarItem[]>();
  for (const i of items) if (i.kind === 'scheduled_run') out.set(i.startDay, [...(out.get(i.startDay) ?? []), i]);
  return out;
};
export const overlapsByDay = (overlaps: CalendarOverlap[]) => {
  const out = new Map<string, CalendarOverlap[]>();
  for (const o of overlaps) out.set(o.day, [...(out.get(o.day) ?? []), o]);
  return out;
};

/** Seven day columns with their runs; send windows as bars across the days they cover. */
export function WeekView({ environmentId, from, to, items, overlaps, today }: CalendarBodyProps) {
  const days = daysOf(from, to);
  const runs = runsByDay(items);
  const byDay = overlapsByDay(overlaps);
  const byKey = new Map(items.map((i) => [i.key, i]));
  const windows = items.filter((i) => i.kind === 'active_window');
  return (
    <div className="calendar-week">
      {windows.length > 0 && (
        <ul className="calendar-week__windows" aria-label="Active periods">
          {windows.map((w) => (
            <li key={w.key} className={`calendar__window calendar__window--${w.objectType}`} style={{ gridColumn: `${days.indexOf(w.startDay) + 1} / ${days.indexOf(w.endDay) + 2}` }} title={windowText(w)}>
              <ObjectLink environmentId={environmentId} item={w} /> <span className="calendar__meta">{windowText(w)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="calendar-week__days">
        {days.map((day) => (
          <section key={day} aria-label={dayLabel(day)} className={`calendar__day${day === today ? ' calendar__day--today' : ''}`}>
            <header className="calendar__day-header">
              <span>{dayLabel(day)}</span>
              <OverlapBadge environmentId={environmentId} day={day} overlaps={byDay.get(day) ?? []} items={byKey} />
            </header>
            {(runs.get(day) ?? []).length ? (
              <ul className="calendar__runs">
                {runs.get(day)!.map((item) => (
                  <RunEntry key={item.key} environmentId={environmentId} item={item} />
                ))}
              </ul>
            ) : (
              <p className="hint">No scheduled sends</p>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
